import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import * as d3 from "d3";
import React, { useEffect } from "react";

const locations = [
  { name: "Thiruvananthapuram", coordinates: [76.9, 8.5], type: "district" },
  { name: "Gurgaon", coordinates: [77.0, 28.5], type: "district" },
  { name: "South Goa", coordinates: [74.1, 15.0], type: "district" },
  { name: "Varanasi", coordinates: [83.0, 25.3], type: "district" },
  { name: "Ranchi", coordinates: [85.3, 23.3], type: "district" },
  { name: "Udaipur", coordinates: [73.7, 24.6], type: "district" },
  { name: "Aurangabad", coordinates: [75.3, 19.9], type: "district" },
  { name: "Madurai", coordinates: [78.1, 9.9], type: "district" },
  { name: "Shimla", coordinates: [77.2, 31.1], type: "district" },
  { name: "Coimbatore", coordinates: [76.9, 11.0], type: "district" },
  { name: "Jaipur", coordinates: [75.8, 26.9], type: "district" },
  { name: "Guwahati", coordinates: [91.7, 26.1], type: "district" },
  { name: "Bhubaneswar", coordinates: [85.8, 20.3], type: "district" },
  { name: "Lucknow", coordinates: [80.9, 26.8], type: "district" },
  { name: "Kozhikode", coordinates: [75.8, 11.3], type: "district" },
  { name: "Indore", coordinates: [75.8, 22.7], type: "district" },
  { name: "Amritsar", coordinates: [74.9, 31.6], type: "district" },
  { name: "Visakhapatnam", coordinates: [83.2, 17.7], type: "district" },
  { name: "Nagpur", coordinates: [79.1, 21.1], type: "district" },
  { name: "Mysuru", coordinates: [76.6, 12.3], type: "district" },
  { name: "Mumbai", coordinates: [72.9, 19.1], type: "district" },
  { name: "Delhi", coordinates: [77.1, 28.6], type: "district" },
  { name: "Bengaluru", coordinates: [77.6, 13.0], type: "district" },
  { name: "Hyderabad", coordinates: [78.5, 17.4], type: "district" },
  { name: "Chennai", coordinates: [80.3, 13.1], type: "district" },
  { name: "Kolkata", coordinates: [88.4, 22.5], type: "district" },
  { name: "Ahmedabad", coordinates: [72.6, 23.0], type: "district" },
  { name: "Pune", coordinates: [73.9, 18.5], type: "district" },
  { name: "Surat", coordinates: [72.8, 21.2], type: "district" },
  { name: "Kanpur", coordinates: [80.3, 26.4], type: "district" },
  { name: "Patna", coordinates: [85.1, 25.6], type: "district" },
  { name: "Bhopal", coordinates: [77.4, 23.2], type: "district" },
  { name: "Vadodara", coordinates: [73.2, 22.3], type: "district" },
  { name: "Agra", coordinates: [78.0, 27.2], type: "district" },
  { name: "Jodhpur", coordinates: [73.0, 26.2], type: "district" },
  { name: "Dehradun", coordinates: [78.0, 30.3], type: "district" },
  { name: "Chandigarh", coordinates: [76.8, 30.7], type: "district" },
  { name: "Jabalpur", coordinates: [79.9, 23.2], type: "district" },
  { name: "Raipur", coordinates: [81.6, 21.3], type: "district" },
  { name: "Kochi", coordinates: [76.3, 10.0], type: "district" },
];

const Map = ({ setGetRequestCount, isNightTime }) => {
  const [activeDots, setActiveDots] = React.useState({});
  const [popups, setPopups] = React.useState([]);
  const [nightLocations, setNightLocations] = React.useState([]);
  const mapContainerRef = React.useRef(null);
  const [dimensions, setDimensions] = React.useState({
    width: 800,
    height: 600,
  });

  useEffect(() => {
    // console.log(isNightTime());
  }, [isNightTime]);

  // Update dimensions based on container size
  React.useEffect(() => {
    const updateDimensions = () => {
      if (mapContainerRef.current) {
        const { width } = mapContainerRef.current.getBoundingClientRect();
        const height = (width * 3) / 4;
        setDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  
  // console.log("bgvTimer");

  // Create projection reference
  const projection = React.useMemo(
    () =>
      d3
        .geoMercator()
        .scale((dimensions.width / 800) * 900)
        .center([82, 23])
        .translate([dimensions.width / 2, dimensions.height / 2]),
    [dimensions]
  );

  // Handle request simulations and night location selection
  React.useEffect(() => {
    let requestInterval = null;
    let timeoutId = null;

    const selectNightLocations = () => {
      const numLocations = 10; // Exactly 10 locations
      const shuffledLocations = [...locations].sort(() => Math.random() - 0.5);
      const selected = shuffledLocations.slice(0, numLocations).map((loc) => ({
        id: `${loc.name}-${Date.now()}`,
        coordinates: loc.coordinates,
        name: loc.name,
        type: loc.type,
      }));
      setNightLocations(selected);
    };

    const handleDayRequests = () => {
      requestInterval = setInterval(() => {
        if (isNightTime()) return; // Skip daytime logic during night

        // Clear existing timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        // Daytime: Create popups and active dots
        const numPopups = Math.floor(Math.random() * 5) + 1;
        if (!isNightTime()) {
          setTimeout(() => {
            setGetRequestCount((prev) => prev + numPopups);
          }, 500);
        }
        const shuffledLocations = [...locations].sort(
          () => Math.random() - 0.5
        );
        const selectedLocations = shuffledLocations.slice(0, numPopups);

        const newPopups = selectedLocations.map((loc) => ({
          id: `${loc.name}-${Date.now()}`,
          coordinates: loc.coordinates,
          name: loc.name,
          type: loc.type,
        }));

        setPopups(newPopups);
        setActiveDots(
          selectedLocations.reduce((acc, loc) => {
            acc[loc.name] = true;
            return acc;
          }, {})
        );

        timeoutId = setTimeout(() => {
          setPopups([]);
          setActiveDots({});
        }, 3500);
      }, 2500);
    };

    // Initial setup based on time
    if (isNightTime()) {
      selectNightLocations();
    } else {
      handleDayRequests();
    }

    // Check time every minute to switch modes
    const timeCheckInterval = setInterval(() => {
      if (isNightTime()) {
        // Entering night: Select new random locations and clear daytime elements
        selectNightLocations();
        setPopups([]);
        setActiveDots({});
        if (requestInterval) {
          clearInterval(requestInterval);
          requestInterval = null;
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      } else if (!requestInterval) {
        // Entering day: Start daytime requests
        handleDayRequests();
      }
    }, 60000);

    return () => {
      if (requestInterval) clearInterval(requestInterval);
      if (timeoutId) clearTimeout(timeoutId);
      clearInterval(timeCheckInterval);
    };
  }, [locations, setGetRequestCount]);

  return (
    <div className="relative  w-full h-full" ref={mapContainerRef}>
      <h1 className="bg-white text-center mt-5 ml-5 w-[130px] text-xs font-medium text-[#1179C2] px-4 py-2 rounded-[3px]">
        {isNightTime() ? "Total Requests" : "Live"}
      </h1>
      <div className="map-container">
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            scale: (dimensions.width / 800) * 900,
            center: [82, 23],
          }}
          width={dimensions.width}
          height={dimensions.height}
        >
          <Geographies geography="/india-composite.json">
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#f0f0f0"
                  stroke="#333"
                  strokeWidth={geo.properties.type === "district" ? 0.3 : 0.5}
                  style={{
                    default: { outline: "none" },
                    hover: { outline: "none" },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>

          {isNightTime()
            ? // Night: Static yellow dots at 10 random locations with text
              nightLocations.map((loc) => (
                <Marker key={loc.id} coordinates={loc.coordinates}>
                  <circle
                    r={loc.type === "city" ? 8 : 6} // Increased size
                    fill="#FFD700" // Yellow for night
                    className="yellow-dots"
                  />
                </Marker>
              ))
            : // Day: Blinking green dots for active requests
              locations.map((loc) => (
                <Marker key={loc.name} coordinates={loc.coordinates}>
                  {activeDots[loc.name] && (
                    <circle
                      r={loc.type === "city" ? 6 : 4}
                      fill="#34d399"
                      className="blinking-dot"
                    />
                  )}
                </Marker>
              ))}
        </ComposableMap>

        {!isNightTime() &&
          popups.map((popup) => (
            <div
              key={popup.id}
              className="popup"
              style={{
                position: "absolute",
                left: `${Math.min(
                  Math.max(projection(popup.coordinates)[0] + 10, 10),
                  dimensions.width - 150
                )}px`,
                top: `${Math.max(
                  projection(popup.coordinates)[1] - -34,
                  10
                )}px`,
                pointerEvents: "none",
                zIndex: 10,
              }}
            >
              New Request Initiated
            </div>
          ))}
        {isNightTime() && (
          <div class="flex items-center gap-2 py-1 text-sm  justify-center w-[150px]  md:w-[160px]  lg:w-[160px] relative left-[54%] -top-16 md:left-[65%] md:-top-28 lg:left-[65%] lg:-top-28 border rounded-md">
            <span class="w-3 h-3 bg-[#FFD700] rounded-full"></span>
            Request initiated
          </div>
        )}
      </div>
    </div>
  );
};

export default Map;
